import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { ArrowUpRight, CheckCircle2, Home as HomeIcon, Sparkles } from "lucide-react";
import BeforeAfterGallery from "@/components/BeforeAfterGallery";
import PageHead from "@/components/PageHead";
import { api, fileUrl } from "@/lib/api";

const FALLBACK_TRANSFORMACOES = [
    {
        title: "Cozinha renovada",
        subtitle: "Apartamento T2 · Oeiras",
        before: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
        after: "https://images.unsplash.com/photo-1714860534425-7ce04e013dec?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    },
    {
        title: "Casa de banho premium",
        subtitle: "Moradia · Cascais",
        before: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
        after: "https://images.unsplash.com/photo-1763485955998-a9284d042d50?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    },
    {
        title: "Sala e acabamentos",
        subtitle: "Apartamento T3 · Lisboa",
        before: "https://images.unsplash.com/photo-1554995207-c18c203602cb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
        after: "https://images.unsplash.com/photo-1738168279272-c08d6dd22002?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    },
];
const HERO = "https://images.unsplash.com/photo-1738168279272-c08d6dd22002?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

const INCLUDED = [
    "Demolições e remoção de entulho",
    "Alterações de canalização e eletricidade",
    "Pladur, tetos falsos e isolamento",
    "Pavimentos e revestimentos",
    "Pintura de paredes, tetos e madeiras",
    "Cozinha, casas de banho e carpintaria",
    "Coordenação de especialidades quando necessário",
    "Atualizações da obra com fotografias",
    "Vistoria final e limpeza",
];

export default function Remodelacoes() {
    const [transformacoes, setTransformacoes] = useState(FALLBACK_TRANSFORMACOES);
    useEffect(() => {
        api.get("/before-after").then((r) => {
            if (r.data?.length) {
                setTransformacoes(r.data.map((it) => ({
                    title: it.title,
                    subtitle: it.subtitle,
                    before: fileUrl(it.before_id),
                    after: fileUrl(it.after_id),
                })));
            }
        }).catch(() => {});
    }, []);
    return (
        <>
            <PageHead
                title="Remodelações completas — chave-na-mão | O Homem da Obra"
                description="Remodelações integrais de apartamentos e moradias em Lisboa, Cascais, Oeiras, Sintra e distritos vizinhos. Um interlocutor, orçamento claro e obra bem feita."
                canonical="https://ohomemdaobra.pt/remodelacoes"
            />
            <section className="relative">
                <div className="max-w-[1400px] mx-auto px-3 sm:px-4">
                    <div className="relative rounded-[2rem] overflow-hidden min-h-[65vh] flex items-end">
                        <img src={HERO} alt="Remodelação" className="absolute inset-0 w-full h-full object-cover" />
                        <div className="absolute inset-0 hero-mask-forest" />
                        <div className="relative p-8 sm:p-14 lg:p-20 text-white max-w-3xl">
                            <div className="eyebrow-light mb-6"><HomeIcon className="w-3.5 h-3.5" /> Remodelações</div>
                            <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.02]">
                                Remodelações completas,<br /><span className="font-serif-italic">chave-na-mão</span>.
                            </h1>
                            <p className="text-white/85 text-lg mt-6 max-w-xl">
                                Da conceção à entrega, com um interlocutor único, orçamento discriminado e a obra acompanhada passo a passo.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-14 sm:py-20 bg-white">
                <div className="max-w-6xl mx-auto px-6 sm:px-10 grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="eyebrow mb-6"><CheckCircle2 className="w-3.5 h-3.5" /> O que inclui</div>
                        <h2 className="font-display text-3xl sm:text-5xl font-bold text-stone-900 tracking-tight mb-6">
                            Uma abordagem <span className="font-serif-italic text-[hsl(var(--moss))]">integrada</span>.
                        </h2>
                        <p className="text-stone-600 text-lg leading-relaxed mb-6">
                            Numa remodelação completa coordenamos todas as fases da obra: demolições, infraestruturas, acabamentos e limpeza final. Colaboramos com profissionais especializados quando o trabalho o exige, mantendo sempre o acompanhamento e a responsabilidade da coordenação.
                        </p>
                        <ul className="space-y-3">
                            {INCLUDED.map((i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <CheckCircle2 className="w-5 h-5 text-[hsl(var(--moss))] flex-shrink-0 mt-0.5" />
                                    <span className="text-stone-700">{i}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="rounded-[2rem] overflow-hidden aspect-[4/3]">
                        <img src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Remodelação" className="w-full h-full object-cover" loading="lazy" />
                    </div>
                </div>
            </section>

            {/* ANTES & DEPOIS — GALERIA COMPLETA */}
            <section className="py-14 sm:py-20">
                <div className="max-w-6xl mx-auto px-6 sm:px-10">
                    <div className="eyebrow mb-6"><Sparkles className="w-3.5 h-3.5" /> Antes & Depois</div>
                    <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight leading-[1.05] max-w-3xl mb-4">
                        A diferença que uma <span className="font-serif-italic text-[hsl(var(--copper))]">boa obra</span> faz.
                    </h2>
                    <p className="text-[hsl(var(--graphite))]/70 text-lg max-w-2xl mb-10">
                        Arraste o cursor sobre a imagem ou utilize as setas para percorrer alguns dos nossos trabalhos.
                    </p>
                    <BeforeAfterGallery items={transformacoes} />
                </div>
            </section>

            <section className="py-20">
                <div className="max-w-[1400px] mx-auto px-3 sm:px-4">
                    <div className="bg-[hsl(var(--moss))] rounded-[2rem] p-10 sm:p-16 text-center text-white">
                        <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight max-w-2xl mx-auto">
                            Está a pensar remodelar a sua <span className="font-serif-italic">casa</span>?
                        </h2>
                        <p className="text-white/80 text-lg mt-6 max-w-xl mx-auto">Conte-nos o que pretende fazer e analisamos consigo os próximos passos.</p>
                        <Link to="/orcamento" className="mt-8 inline-flex items-center gap-3 bg-white text-[hsl(var(--moss))] px-6 py-3 rounded-full font-medium hover:bg-white/90 transition">
                            Pedir orçamento
                            <span className="w-6 h-6 bg-[hsl(var(--moss))] text-white rounded-full flex items-center justify-center"><ArrowUpRight className="w-3.5 h-3.5" /></span>
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}
